'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import Toggle from '@/components/ui/Toggle';
import { toast } from 'sonner';

interface ScheduleFormData {
  reportTemplate: string;
  format: 'pdf' | 'csv' | 'json';
  frequency: 'daily' | 'weekly' | 'monthly' | 'once';
  dayOfWeek?: string;
  dayOfMonth?: string;
  timeUTC: string;
  dateRangeType: 'last-24h' | 'last-7d' | 'last-30d' | 'last-quarter' | 'custom';
  customStartDate?: string;
  customEndDate?: string;
  recipients: string;
  includeRawData: boolean;
  compressOutput: boolean;
}

interface ScheduledReport {
  id: string;
  name: string;
  frequency: string;
  nextRun: string;
  format: string;
  recipients: number;
  enabled: boolean;
}

const scheduledReports: ScheduledReport[] = [
  { id: 'SCH-001', name: 'ISO 27001 — Security Incident Register', frequency: 'Weekly (Mon 06:00)', nextRun: '2026-03-23 06:00 UTC', format: 'PDF', recipients: 3, enabled: true },
  { id: 'SCH-002', name: 'Daily Threat Summary', frequency: 'Daily (06:00)', nextRun: '2026-03-17 06:00 UTC', format: 'PDF', recipients: 5, enabled: true },
  { id: 'SCH-003', name: 'PCI-DSS Requirement 10 — Audit Log Report', frequency: 'Weekly (Mon 06:00)', nextRun: '2026-03-23 06:00 UTC', format: 'CSV', recipients: 2, enabled: true },
  { id: 'SCH-004', name: 'Network Traffic Anomaly Report', frequency: 'Daily (00:00)', nextRun: '2026-03-17 00:00 UTC', format: 'CSV', recipients: 2, enabled: false },
];

export default function ScheduleReportForm() {
  const [schedules, setSchedules] = useState(scheduledReports);
  const [includeRawData, setIncludeRawData] = useState(false);
  const [compressOutput, setCompressOutput] = useState(true);

  const { register, handleSubmit, watch, reset, formState: { errors, isSubmitting } } = useForm<ScheduleFormData>({
    defaultValues: {
      reportTemplate: '',
      format: 'pdf',
      frequency: 'weekly',
      dayOfWeek: 'monday',
      timeUTC: '06:00',
      dateRangeType: 'last-7d',
      recipients: '',
      includeRawData: false,
      compressOutput: true,
    },
  });

  const frequency = watch('frequency');
  const dateRangeType = watch('dateRangeType');

  const onSubmit = async (data: ScheduleFormData) => {
    // Backend integration: POST /api/reports/schedules { ...data }
    await new Promise((r) => setTimeout(r, 600));
    toast.success('Report schedule created — first run at ' + data.timeUTC + ' UTC');
    reset();
  };

  const handleToggleSchedule = (id: string, val: boolean) => {
    // Backend integration: PATCH /api/reports/schedules/:id { enabled: val }
    setSchedules((prev) => prev.map((s) => s.id === id ? { ...s, enabled: val } : s));
    const sch = schedules.find((s) => s.id === id);
    toast.success(`Schedule "${sch?.name}" ${val ? 'enabled' : 'paused'}`);
  };

  const handleDeleteSchedule = (id: string) => {
    // Backend integration: DELETE /api/reports/schedules/:id
    const sch = schedules.find((s) => s.id === id);
    setSchedules((prev) => prev.filter((s) => s.id !== id));
    toast.success(`Schedule "${sch?.name}" deleted`);
  };

  const inputClass = "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-lg text-sm text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30 transition-colors";
  const labelClass = "block text-xs font-semibold text-zinc-400 mb-1.5";
  const errorClass = "mt-1 text-xs text-red-400";
  const helperClass = "mt-1 text-[11px] text-zinc-600";

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 2xl:grid-cols-5 gap-6">
      {/* Existing Schedules */}
      <div className="xl:col-span-3 2xl:col-span-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-800">
            <h3 className="text-sm font-semibold text-zinc-200">Active Schedules</h3>
            <span className="text-xs text-zinc-600 font-mono">{schedules.filter(s => s.enabled).length} active</span>
          </div>
          <div className="divide-y divide-zinc-800/60">
            {schedules.map((sch) => (
              <div key={sch.id} className={`flex items-start gap-4 px-5 py-4 hover:bg-zinc-800/20 transition-colors group ${!sch.enabled ? 'opacity-60' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-zinc-200 truncate">{sch.name}</p>
                    {!sch.enabled && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide bg-zinc-800 text-zinc-500 border border-zinc-700 px-1.5 py-0.5 rounded flex-shrink-0">
                        Paused
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-zinc-600">
                    <span className="flex items-center gap-1">
                      <Icon name="ClockIcon" size={11} />
                      {sch.frequency}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="DocumentTextIcon" size={11} />
                      {sch.format}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="EnvelopeIcon" size={11} />
                      {sch.recipients} recipient{sch.recipients !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-600 mt-1 font-mono">
                    Next run: <span className="text-zinc-500">{sch.nextRun}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Toggle
                    checked={sch.enabled}
                    onChange={(val) => handleToggleSchedule(sch.id, val)}
                    size="sm"
                  />
                  <button
                    onClick={() => handleDeleteSchedule(sch.id)}
                    className="p-1.5 rounded hover:bg-zinc-700 text-zinc-700 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                    aria-label="Delete schedule — this cannot be undone"
                  >
                    <Icon name="TrashIcon" size={13} />
                  </button>
                </div>
              </div>
            ))}
            {schedules.length === 0 && (
              <div className="px-5 py-12 text-center">
                <Icon name="ClockIcon" size={32} className="text-zinc-700 mx-auto mb-3" />
                <p className="text-sm font-medium text-zinc-500">No scheduled reports</p>
                <p className="text-xs text-zinc-600 mt-1">Create a schedule using the form to automate report delivery</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Schedule Form */}
      <div className="xl:col-span-2 2xl:col-span-2">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-zinc-800">
            <h3 className="text-sm font-semibold text-zinc-200">Create New Schedule</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Automate report generation and delivery</p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
            {/* Template */}
            <div>
              <label htmlFor="sched-template" className={labelClass}>
                Report Template <span className="text-red-400">*</span>
              </label>
              <select
                id="sched-template"
                className={inputClass}
                {...register('reportTemplate', { required: 'Select a report template' })}
              >
                <option value="">Select template...</option>
                <optgroup label="PCI-DSS">
                  <option value="RPT-T-001">PCI-DSS Req 10 — Audit Log Report</option>
                  <option value="RPT-T-002">PCI-DSS Vulnerability Scan Summary</option>
                </optgroup>
                <optgroup label="ISO 27001">
                  <option value="RPT-T-003">ISO 27001 — Security Incident Register</option>
                  <option value="RPT-T-004">ISO 27001 — ISMS Performance Metrics</option>
                </optgroup>
                <optgroup label="SOC 2">
                  <option value="RPT-T-005">SOC 2 Type II — Security Monitoring Evidence</option>
                  <option value="RPT-T-006">SOC 2 — Availability & Confidentiality</option>
                </optgroup>
                <optgroup label="Operational">
                  <option value="RPT-T-007">Daily Threat Summary</option>
                  <option value="RPT-T-008">Weekly SOC Operations Report</option>
                  <option value="RPT-T-009">Asset Risk Posture Report</option>
                  <option value="RPT-T-010">MITRE ATT&CK Coverage Map</option>
                  <option value="RPT-T-011">Alert Rule Performance Report</option>
                  <option value="RPT-T-012">Network Traffic Anomaly Report</option>
                </optgroup>
              </select>
              {errors.reportTemplate && <p className={errorClass}>{errors.reportTemplate.message}</p>}
            </div>

            {/* Format */}
            <div>
              <label htmlFor="sched-format" className={labelClass}>Output Format</label>
              <select id="sched-format" className={inputClass} {...register('format')}>
                <option value="pdf">PDF — formatted report</option>
                <option value="csv">CSV — raw data export</option>
                <option value="json">JSON — machine-readable</option>
              </select>
            </div>

            {/* Frequency */}
            <div>
              <label htmlFor="sched-frequency" className={labelClass}>
                Frequency <span className="text-red-400">*</span>
              </label>
              <select id="sched-frequency" className={inputClass} {...register('frequency')}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="once">Once (on-demand at scheduled time)</option>
              </select>
            </div>

            {frequency === 'weekly' && (
              <div>
                <label htmlFor="sched-dow" className={labelClass}>Day of Week</label>
                <select id="sched-dow" className={inputClass} {...register('dayOfWeek')}>
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d) => (
                    <option key={d} value={d.toLowerCase()}>{d}</option>
                  ))}
                </select>
              </div>
            )}

            {frequency === 'monthly' && (
              <div>
                <label htmlFor="sched-dom" className={labelClass}>Day of Month</label>
                <select id="sched-dom" className={inputClass} {...register('dayOfMonth')}>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={String(d)}>{d}</option>
                  ))}
                  <option value="last">Last day of month</option>
                </select>
              </div>
            )}

            {/* Time */}
            <div>
              <label htmlFor="sched-time" className={labelClass}>
                Time (UTC) <span className="text-red-400">*</span>
              </label>
              <input
                id="sched-time"
                type="time"
                className={`${inputClass} font-mono`}
                {...register('timeUTC', { required: 'Time is required' })}
              />
              {errors.timeUTC && <p className={errorClass}>{errors.timeUTC.message}</p>}
            </div>

            {/* Date Range */}
            <div>
              <label htmlFor="sched-daterange" className={labelClass}>Report Date Range</label>
              <select id="sched-daterange" className={inputClass} {...register('dateRangeType')}>
                <option value="last-24h">Last 24 hours</option>
                <option value="last-7d">Last 7 days</option>
                <option value="last-30d">Last 30 days</option>
                <option value="last-quarter">Last quarter</option>
                <option value="custom">Custom range</option>
              </select>
            </div>

            {dateRangeType === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="sched-start" className={labelClass}>Start Date</label>
                  <input type="date" id="sched-start" className={`${inputClass} font-mono text-xs`} {...register('customStartDate')} />
                </div>
                <div>
                  <label htmlFor="sched-end" className={labelClass}>End Date</label>
                  <input type="date" id="sched-end" className={`${inputClass} font-mono text-xs`} {...register('customEndDate')} />
                </div>
              </div>
            )}

            {/* Recipients */}
            <div>
              <label htmlFor="sched-recipients" className={labelClass}>
                Recipients <span className="text-red-400">*</span>
              </label>
              <textarea
                id="sched-recipients"
                rows={2}
                placeholder="selin.arslan@corp.net, soc-lead@corp.net"
                className={`${inputClass} resize-none text-xs font-mono`}
                {...register('recipients', {
                  required: 'At least one recipient is required',
                  pattern: { value: /^[\w.+-]+@[\w-]+\.[a-z]{2,}(,\s*[\w.+-]+@[\w-]+\.[a-z]{2,})*$/, message: 'Enter valid email addresses separated by commas' }
                })}
              />
              {errors.recipients && <p className={errorClass}>{errors.recipients.message}</p>}
              <p className={helperClass}>Comma-separated email addresses</p>
            </div>

            {/* Options */}
            <div className="space-y-2.5 pt-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-400">Include raw event data</p>
                  <p className="text-[11px] text-zinc-600">Attach CSV of raw events to the report</p>
                </div>
                <Toggle checked={includeRawData} onChange={setIncludeRawData} size="sm" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-400">Compress output</p>
                  <p className="text-[11px] text-zinc-600">ZIP archive for email delivery</p>
                </div>
                <Toggle checked={compressOutput} onChange={setCompressOutput} size="sm" />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors active:scale-95 mt-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating Schedule…
                </>
              ) : (
                <>
                  <Icon name="ClockIcon" size={15} />
                  Create Schedule
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}